/**
 * תנאי שימוש — TASKS 4.1 · PRD 8.1.
 *
 * ⭐ 02.09.2026 — הוחלף בנוסח המאושר של עורך הדין (V2, `Legal Documents/V2`).
 *    התוכן זהה למסמך שהתקבל; ההעברה שמרה על:
 *      - תוכן העניינים והעוגנים הפנימיים (`<a href="#...">` ← `<h2 id="...">`)
 *      - כתובת דוא"ל וטלפון כקישורי פעולה (`mailto:` / `tel:`)
 *      - קישור לעמוד "צור קשר" הפנימי
 *    ⛔ העיצוב לא הועבר — רק התוכן הסמנטי (מערכת העיצוב של האתר, Rubik).
 *
 * ⭐ 03.09.2026 — עו"ד אישר את הנוסח (V2). תיבת האזהרה ("טיוטה") הוסרה
 *    לפי אישור אריאנה. שלושה פריטים קטנים ממשיכים בנפרד מול טל: תק-3 (ניסוח
 *    הקבלה הדיגיטלית), פר-5 (סליקה), נג (כותרת רכז) — ⛔ לא חוסמים.
 */
import { Link } from 'react-router-dom';
import {
  COMPANY_ADDRESS,
  COMPANY_LEGAL_NAME,
  EMAIL,
  EMAIL_HREF,
  PHONE_DISPLAY,
  PHONE_TEL_HREF,
} from '../content/businessInfo.js';

const LAST_REVIEWED = '02.09.2026';

const TOC: { id: string; label: string }[] = [
  { id: 'הגדרות-וכללי', label: '1. הגדרות וכללי' },
  { id: 'זכאות-לשימוש', label: '2. זכאות לשימוש' },
  { id: 'ביטול-עסקה-והחזר-כספי-מכר-מרחוק', label: '3. ביטול עסקה והחזר כספי (מכר מרחוק)' },
  { id: 'קבלה-ושובר-זיכוי-דיגיטלי-תיקון-73', label: '4. קבלה ושובר זיכוי דיגיטלי (תיקון 73)' },
  { id: 'אספקה-ומשלוחים', label: '5. אספקה ומשלוחים' },
  { id: 'קניין-רוחני-ותוכן-גולשים', label: '6. קניין רוחני ותוכן גולשים' },
  { id: 'דין-ושיפוט', label: '7. דין ושיפוט' },
  { id: 'יישוב-סכסוכים', label: '8. יישוב סכסוכים' },
  { id: 'שינויים-ועדכונים', label: '9. שינויים ועדכונים' },
  { id: 'כוח-עליון', label: '10. כוח עליון' },
  { id: 'הפרדת-סעיפים', label: '11. הפרדת סעיפים' },
  { id: 'כללי-והמחאת-זכויות', label: '12. כללי והמחאת זכויות' },
];

export function TermsPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="text-3xl font-bold">תנאי שימוש</h1>

      <p className="text-muted mt-2 text-sm">
        {COMPANY_LEGAL_NAME} · כתובת רשומה: {COMPANY_ADDRESS} · דוא"ל לפניות:{' '}
        <a href={EMAIL_HREF} className="ltr-inline text-link underline">
          {EMAIL}
        </a>
      </p>

      <nav
        aria-labelledby="toc-heading"
        className="border-line bg-surface mt-8 rounded-md border p-4"
      >
        <h2 id="toc-heading" className="text-lg font-bold">
          תוכן עניינים
        </h2>
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
          {TOC.map((t) => (
            <li key={t.id}>
              <a href={`#${t.id}`} className="text-link underline">
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-8 space-y-6 leading-relaxed">
        <section>
          <h2 id="הגדרות-וכללי" className="text-xl font-bold">
            1. הגדרות וכללי
          </h2>
          <p className="text-muted mt-2">
            1.1. ברוכים הבאים לאתר {COMPANY_LEGAL_NAME} (להלן: "האתר" או "העסק").
          </p>
          <p className="text-muted mt-2">1.2. כתובת העסק: {COMPANY_ADDRESS}.</p>
          <p className="text-muted mt-2">
            1.3. המסמך שלפניך ("תנאי השימוש") מסדיר את היחסים המשפטיים בין העסק לבין כל אדם העושה
            שימוש באתר.
          </p>
          <p className="text-muted mt-2">
            1.4. השימוש באתר מהווה הסכמה מלאה, מדעת ובלתי חוזרת לכל האמור בתנאי שימוש אלו.
          </p>
        </section>

        <section>
          <h2 id="זכאות-לשימוש" className="text-xl font-bold">
            2. זכאות לשימוש
          </h2>
          <p className="text-muted mt-2">2.1. השימוש באתר מותר למשתמשים מעל גיל 18 בלבד.</p>
          <p className="text-muted mt-2">
            2.2. <strong className="text-ink">חסימת משתמשים:</strong> העסק שומר לעצמו את הזכות
            להשעות או לחסום גישה של משתמשים לשירותיו במקרה של חשד סביר להפרה יסודית של תנאי השימוש.
            ככל שהדבר מתאפשר בנסיבות העניין, העסק יבצע מאמץ סביר למסור הודעה מוקדמת לפני חסימה
            כאמור, למעט במקרים של הפרה חמורה, זיוף, פגיעה באבטחה או דרישה חוקית מיידית. מובהר כי
            העסק לא יישא באחריות לנזק הנובע מחסימה מוצדקת שבוצעה בתום לב בהתאם לתנאים אלו.
          </p>
          <h3 className="text-ink mt-3 font-semibold">2.3. שימוש אסור</h3>
          <p className="text-muted mt-2">מבלי לגרוע מהאמור לעיל, חל איסור מוחלט על:</p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              העתקה, שכפול, הפצה, שיווק או שימוש מסחרי בתכנים המוצגים באתר ללא אישור מראש ובכתב.
            </li>
            <li>הצגת תכנים מהאתר באתר אחר ללא אישור בכתב מהעסק.</li>
            <li>
              שימוש בשמות מסחריים, סימנים מסחריים, לוגואים או מידע קנייני אחר של העסק ללא אישור.
            </li>
            <li>ביצוע פעולות אלימות, הטרדה, איומים או לשון הרע באמצעות האתר.</li>
            <li>העלאת תוכן פוגע, בלתי חוקי, מטעה או מפר זכויות צד שלישי.</li>
            <li>עקיפת אמצעי אבטחה או פגיעה בתפקודו התקין של האתר.</li>
          </ul>
        </section>

        <section>
          <h2 id="ביטול-עסקה-והחזר-כספי-מכר-מרחוק" className="text-xl font-bold">
            3. ביטול עסקה והחזר כספי (מכר מרחוק)
          </h2>
          <p className="text-muted mt-2">
            3.1. מובהר כי הזמנת מוצרים באמצעות האתר מהווה עסקת מכר מרחוק כמשמעותה בחוק הגנת הצרכן,
            התשמ"א-1981, גם כאשר התשלום עצמו אינו מתבצע באתר. הוראות פרק זה יחולו על כל עסקה כאמור.
          </p>
          <p className="text-muted mt-2">
            3.2. <strong className="text-ink">זכות הביטול:</strong> בהתאם לסעיף 14ג לחוק הגנת הצרכן,
            הצרכן רשאי לבטל עסקת מכר מרחוק בתוך 14 ימים מיום קבלת הנכס, או מיום קבלת המסמך המכיל את
            פרטי העסקה (המאוחר מביניהם). תנאי הביטול המלאים מפורטים בפרק זה.
          </p>
          <p className="text-muted mt-2">
            <strong className="text-ink">הרחבת זכות ביטול (תיקון 40 - אוכלוסיות מוגנות):</strong>{' '}
            צרכן שהוא אדם עם מוגבלות (כהגדרתו בחוק שוויון זכויות לאנשים עם מוגבלות), אזרח ותיק
            (שמלאו לו 65) או עולה חדש, זכאי לבטל את העסקה בתוך 4 חודשים (ובלבד שההתקשרות כללה שיחה).
            העסק רשאי לדרוש הצגת תעודה מתאימה.
          </p>
          <p className="text-muted mt-2">
            3.3. <strong className="text-ink">דרכי ביטול:</strong> הודעה על ביטול העסקה יכולה להינתן
            באחת או יותר מהדרכים הבאות הנהוגות על ידי העסק:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              באמצעות דואר אלקטרוני לכתובת:{' '}
              <a href={EMAIL_HREF} className="ltr-inline text-link underline">
                {EMAIL}
              </a>
            </li>
            <li>באמצעות דואר רשום לכתובת: {COMPANY_ADDRESS}</li>
            <li>
              באמצעות הטלפון:{' '}
              <a href={PHONE_TEL_HREF} className="ltr-inline text-link underline">
                {PHONE_DISPLAY}
              </a>
            </li>
          </ul>
          <p className="text-muted mt-2">
            עם קבלת הודעת הביטול, תישלח ללקוח הודעת אישור בכתב בתוך 14 ימים, באחד מאמצעי התקשורת
            המפורטים לעיל (§14ג(ב) לחוק הגנת הצרכן).
          </p>
          <p className="text-muted mt-2">
            3.4. <strong className="text-ink">תוצאות הביטול ודמי ביטול:</strong> במקרה של ביטול שלא
            עקב פגם או אי-התאמה, הצרכן יחזיר את הנכס על חשבונו, והעסק יהיה רשאי לגבות דמי ביטול כדין
            - בהתאם להוראות החוק (5% או 100 ש"ח). במקרה של פגם במוצר, לא ייגבו דמי ביטול והחזרת הנכס
            תעשה על חשבון העסק.
          </p>
          <p className="text-muted mt-2">
            3.5. <strong className="text-ink">החזר כספי:</strong> החזר כספי יינתן בתוך 14 ימים מיום
            קבלת הודעת הביטול, באופן שבו שולמה התמורה בעסקה.
          </p>
          <p className="text-muted mt-2">
            <strong className="text-ink">סייגים לזכות הביטול (מוצרים שאינם ניתנים להחזרה):</strong>{' '}
            טובין שיוצרו במיוחד עבור הצרכן לפי מידות או דרישות מיוחדות.
          </p>
          <p className="text-muted mt-2">
            עסקה הנעשית באמצעות האתר על ידי עוסק, חברה, תאגיד, עצמאי או כל גורם אחר הרוכש את
            השירותים או המוצרים לצורך עסקי, מסחרי או מקצועי, תיחשב לעסקה עסקית שאינה עסקה צרכנית.
            בהתאם, ובכפוף להוראות כל דין, הוראות חוק הגנת הצרכן, התשמ"א-1981, לרבות הוראות הנוגעות
            לביטול עסקה, לא יחולו על עסקאות כאמור.
          </p>
        </section>

        <section>
          <h2 id="קבלה-ושובר-זיכוי-דיגיטלי-תיקון-73" className="text-xl font-bold">
            4. קבלה ושובר זיכוי דיגיטלי (תיקון 73)
          </h2>
          <p className="text-muted mt-2">
            4.1. <strong className="text-ink">קבלה דיגיטלית:</strong> במקרה של חיובים, העסק מנפיק
            קבלה דיגיטלית ומסמך אלקטרוני כחוק לתיעוד עסקה, בהתאם לדרישות תיקון 73 לחוק הגנת הצרכן.
          </p>
        </section>

        <section>
          <h2 id="אספקה-ומשלוחים" className="text-xl font-bold">
            5. אספקה ומשלוחים
          </h2>
          <p className="text-muted mt-2">
            5.1. <strong className="text-ink">מדיניות משלוח:</strong> משלוח חינם בהזמנות מעל ₪6,000.
          </p>
          <p className="text-muted mt-2">5.2. זמן אספקה משוער: עד 60 ימי עסקים.</p>
          <p className="text-muted mt-2">
            5.3. <strong className="text-ink">טעות בכתובת או אי-איסוף:</strong> במקרה בו הלקוח הזין
            פרטי כתובת שגויים, או לחלופין לא אסף את החבילה מנקודת האיסוף/הלוקר במועד שנקבע והחבילה
            הוחזרה לשולח, הלקוח יישא במלוא העלויות בגין דמי המשלוח החוזר ו/או דמי הטיפול הכרוכים
            בשילוח מחדש.
          </p>
          <p className="text-muted mt-2">
            5.4. <strong className="text-ink">כוח עליון:</strong> העסק לא יהיה אחראי לאיחור באספקה,
            אי-הספקת שירות או נזק הנובע מנסיבות של כוח עליון, לרבות (אך לא רק) מלחמה, פעולות איבה,
            מצב חירום ביטחוני, גיוס מילואים (לרבות צו 8), שביתות, אסונות טבע, או תקלות במערכות
            התקשורת הארציות.
          </p>
        </section>

        <section>
          <h2 id="קניין-רוחני-ותוכן-גולשים" className="text-xl font-bold">
            6. קניין רוחני ותוכן גולשים
          </h2>
          <p className="text-muted mt-2">
            6.1. <strong className="text-ink">קניין רוחני:</strong> כל זכויות הקניין הרוחני באתר,
            לרבות (אך לא רק): צילומים / עיצובים מקוריים, סימני מסחר / לוגו (למעט תוכן גולשים),
            שייכות ל-{COMPANY_LEGAL_NAME} בלבד (או לצדדים שלישיים שהתירו לעסק להשתמש בהם).
          </p>
          <p className="text-muted mt-2">
            6.1.1. <strong className="text-ink">איסורים מוחלטים:</strong> אין להעתיק, לשכפל, להפיץ,
            לפרסם, להציג בפומבי או להשתמש בחומרים אלו למטרה מסחרית או אחרת ללא קבלת רשות מפורשת
            בכתב.
          </p>
          <p className="text-muted mt-2">
            6.1.2.{' '}
            <strong className="text-ink">
              איסור סריקה ואימון בינה מלאכותית (Anti-Scraping and AI):
            </strong>{' '}
            חל איסור מוחלט לעשות שימוש בבוטים (Bots), עכבישים (Spiders), מערכות סריקה (Crawlers) או
            כל אמצעי אוטומטי אחר לשם כריית מידע (Data Mining), חילוץ נתונים או צילום מסד הנתונים של
            האתר. כמו כן, חל איסור לנצל את התכנים באתר לצורך אימון מודלים של בינה מלאכותית (Machine
            Learning/LLM) ללא רשות מפורשת בכתב.
          </p>
          <h3 className="text-ink mt-3 font-semibold">6.2. הגבלת אחריות ושיפוי</h3>
          <p className="text-muted mt-2">
            6.2.1. <strong className="text-ink">השירות ניתן כפי שהוא:</strong> המוצרים והשירותים
            המוצעים באתר, לרבות הצעות מחיר ומפרטים טכניים, ניתנים לשימוש כפי שהם (AS IS / AS
            AVAILABLE). העסק אינו מתחייב שהשירות יהיה חסין מוחלט מפני תקלות, אך עושה מאמצים סבירים
            לתפעול תקין.
          </p>
          <p className="text-muted mt-2">
            למען הסר ספק, ובכפוף לכל דין מחייב, הגבלת האחריות המפורטת לעיל לא תחול במקרים הבאים:
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>הפרת חובת פרטיות מצד העסק</li>
            <li>נזק גוף או מוות הנובעים מרשלנות</li>
            <li>מעשה מכוון או רשלנות חמורה של העסק</li>
            <li>נזקים אחרים שהחוק אינו מאפשר להגביל את האחריות בגינם</li>
          </ul>
          <p className="text-muted mt-2">
            6.2.2. המשתמש מצהיר ומתחייב כי הוא האחראי הבלעדי לכל שימוש לרעה באתר ו/או הפרה של תנאי
            שימוש אלו.
          </p>
          <p className="text-muted mt-2">
            6.2.3. <strong className="text-ink">הגבלת אחריות ותקרת פיצוי:</strong> בכפוף לכל דין
            מחייב שאינו ניתן להתנייה, אחריות העסק בגין נזקים ישירים המוכחים כנובעים ישירות ממחדל או
            רשלנות של העסק תהיה מוגבלת. חבותו הכוללת של העסק כלפי המשתמש בגין נזקים ישירים המוכחים
            כדין לא תעלה על הסכום ששולם על ידי המשתמש ב-12 החודשים שקדמו לאירוע, או סך של 10,000
            ש"ח, לפי הגבוה מביניהם.
          </p>
          <p className="text-muted mt-2">
            למען הסר ספק, אין באמור כדי להגביל אחריות בגין נזקי גוף, הפרת פרטיות, מעשה זדון, או
            נזקים שאינם ניתנים להתניה על פי דין.
          </p>
          <p className="text-muted mt-2">
            6.3. <strong className="text-ink">אחריות למוצרים:</strong>
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>
              התמונות המוצגות באתר הן להמחשה בלבד. ייתכנו הבדלים בין המראה ומפרט המוצר באתר לבין
              המוצר הסופי. הקובע הוא המפרט הטכני.
            </li>
            <li>
              העסק אינו אחראי לכל נזק שייגרם כתוצאה משימוש במוצר בניגוד להוראות היצרן או עקב שימוש
              בלתי סביר במוצר.
            </li>
            <li>העסק אינו אחראי לעיכובים באספקה הנובעים מנסיבות שאינן בשליטתו.</li>
          </ul>
          <h3 className="text-ink mt-3 font-semibold">6.4. שיפוי</h3>
          <p className="text-muted mt-2">
            המשתמש מתחייב לשפות את העסק בגין כל נזק או הוצאה סבירה (לרבות שכ"ט עו"ד סביר) שייגרמו לו
            עקב הפרה יסודית של תנאי השימוש על ידו באופן ישיר, או עקב שימוש בלתי חוקי באתר שבוצע על
            ידו, ולא בגין תביעות רגולטוריות או מחדלים של העסק.
          </p>
          <h3 className="text-ink mt-3 font-semibold">6.5. קישורים לשירותי צד שלישי</h3>
          <p className="text-muted mt-2">
            6.5.1. <strong className="text-ink">קישורים ושירותים חיצוניים:</strong> האתר עשוי לכלול
            קישורים או שילוב של שירותי צד שלישי (לרבות, למשל, פנייה באמצעות WhatsApp או רשתות
            חברתיות). העסק אינו אחראי לתוכן, לזמינות, לתנאי השימוש או למדיניות הפרטיות של שירותים
            חיצוניים אלו, והשימוש בהם כפוף לתנאי הצדדים השלישיים המפעילים אותם. מובהר כי הוראות בדבר
            הגבלת גישה וחסימת משתמשים מפורטות בסעיף 2.2 לעיל.
          </p>
        </section>

        <section>
          <h2 id="דין-ושיפוט" className="text-xl font-bold">
            7. דין ושיפוט
          </h2>
          <p className="text-muted mt-2">7.1. על הסכם זה יחול הדין הישראלי בלבד.</p>
          <p className="text-muted mt-2">
            7.2. סמכות השיפוט בכל עניין הנובע מהסכם זה תהיה נתונה לבית המשפט המוסמך במחוז הדרום (באר
            שבע), וזאת מבלי לגרוע מזכותו של צרכן להגיש תביעה בבית המשפט המוסמך לפי דין, ובפרט במקום
            מגוריו או מקום עסקו של הנתבע, בהתאם לתקנות סדר הדין האזרחי.
          </p>
        </section>

        <section>
          <h2 id="יישוב-סכסוכים" className="text-xl font-bold">
            8. יישוב סכסוכים
          </h2>
          <p className="text-muted mt-2">
            8.1. <strong className="text-ink">פנייה ראשונית:</strong> בכל מקרה של מחלוקת או תלונה,
            אנו מזמינים אותך לפנות אלינו תחילה בכתובת{' '}
            <a href={EMAIL_HREF} className="ltr-inline text-link underline">
              {EMAIL}
            </a>{' '}
            לצורך ניסיון לפתור את העניין בדרכי נועם מחוץ לכותלי בית המשפט.
          </p>
          <p className="text-muted mt-2">
            8.2. <strong className="text-ink">הליכים משפטיים:</strong> במידה ולא ניתן יהיה לפתור את
            המחלוקת במסגרת שירות הלקוחות, כל סכסוך ידון בכפוף לחוקי מדינת ישראל.
          </p>
          <p className="text-muted mt-2">
            8.3. <strong className="text-ink">גישור ובוררות:</strong> במידה ולא הגענו לפתרון מוסכם,
            הצדדים רשאים לפנות להליך גישור או בוררות בפני גוף מוסמך בישראל, לפני נקיטת הליכים
            משפטיים.
          </p>
          <p className="text-muted mt-2">
            8.4. <strong className="text-ink">תביעות קטנות:</strong> האמור לעיל אינו מונע מהצרכן
            לפנות לבית משפט לתביעות קטנות בהתאם לחוק.
          </p>
        </section>

        <section>
          <h2 id="שינויים-ועדכונים" className="text-xl font-bold">
            9. שינויים ועדכונים
          </h2>
          <p className="text-muted mt-2">
            9.1. העסק שומר לעצמו את הזכות לעדכן את תנאי שימוש אלה מעת לעת, בהתאם לצרכים העסקיים או
            שינויים בדין.
          </p>
          <p className="text-muted mt-2">
            9.2. <strong className="text-ink">הודעה על שינויים מהותיים:</strong> במקרה של שינוי
            מהותי המשפיע על זכויות המשתמש, העסק מבהיר כי תישלח הודעה מראש של 30 ימים לפחות באמצעות
            פרסום בולט באתר, וככל שנמסרה כתובת דואר אלקטרוני בעת הפנייה - גם באמצעות הודעה לאותה
            כתובת. המשך השימוש באתר לאחר מועד כניסת השינויים לתוקף יהווה הסכמה לתנאים המעודכנים.
          </p>
          <p className="text-muted mt-2">
            9.3. משתמש שאינו מסכים לשינוי המהותי, רשאי להפסיק את השימוש באתר לפני מועד כניסת
            השינויים לתוקף.
          </p>
        </section>

        <section>
          <h2 id="כוח-עליון" className="text-xl font-bold">
            10. כוח עליון
          </h2>
          <p className="text-muted mt-2">
            10.1. העסק לא יישא באחריות כלשהי בגין אי-קיום או עיכוב בקיום התחייבויותיו על פי תנאים
            אלה, במידה שהדבר נובע מנסיבות שאינן בשליטתו הסבירה, לרבות (אך לא רק):
          </p>
          <ul className="text-muted mt-2 list-inside list-disc space-y-1">
            <li>מלחמה, פעולות טרור, מצב חירום ביטחוני או מבצע צבאי;</li>
            <li>מגיפה, אסון טבע, רעידת אדמה, שיטפון או תנאי מזג אוויר קיצוניים;</li>
            <li>שביתה, השבתה או עיצומים (למעט של עובדי העסק);</li>
            <li>תקלות בתשתיות תקשורת, חשמל, אינטרנט או מערכות מחשוב שאינן בשליטתנו;</li>
            <li>צווים ממשלתיים, שינויי חקיקה או הוראות רגולטוריות בלתי צפויות.</li>
          </ul>
          <p className="text-muted mt-2">
            10.2. במקרה של כוח עליון, מועדי הביצוע יידחו בהתאם לתקופת האירוע.
          </p>
        </section>

        <section>
          <h2 id="הפרדת-סעיפים" className="text-xl font-bold">
            11. הפרדת סעיפים
          </h2>
          <p className="text-muted mt-2">
            11.1. אם ייקבע על ידי ערכאה מוסמכת כי הוראה כלשהי מתנאי שימוש אלה הינה בטלה, בלתי חוקית
            או בלתי ניתנת לאכיפה, לא ייפגע בכך תוקפן של יתר ההוראות, אשר ימשיכו לעמוד בתוקפן המלא.
          </p>
          <p className="text-muted mt-2">
            11.2. במקרה כאמור, ההוראה הבטלה תוחלף בהוראה חלופית תקפה, הקרובה ככל הניתן לכוונת הצדדים
            המקורית.
          </p>
        </section>

        <section>
          <h2 id="כללי-והמחאת-זכויות" className="text-xl font-bold">
            12. כללי והמחאת זכויות
          </h2>
          <p className="text-muted mt-2">
            12.1. אי-מימוש או שיהוי במימוש זכות מזכויותיו של העסק על פי תנאים אלו ו/או על פי דין, לא
            ייחשב כויתור משתמע על אותה זכות (No Waiver).
          </p>
          <p className="text-muted mt-2">
            12.2. <strong className="text-ink">המחאת זכויות וחובות העסק:</strong> העסק רשאי להמחות
            או להעביר את זכויותיו והתחייבויותיו על פי תנאים אלו לכל צד שלישי, וזאת בכפוף למתן הודעה
            מראש של 30 ימים לפחות למשתמש. במקרה שהמשתמש אינו מסכים להמחאה כאמור, הוא רשאי להפסיק את
            השימוש באתר ולסיים את ההתקשרות לפני מועד ההמחאה.
          </p>
          <p className="text-muted mt-2">
            12.3. המשתמש אינו רשאי להמחות, להעביר או להסב את זכויותיו ו/או חובותיו על פי תנאים אלה
            ללא הסכמת העסק מראש ובכתב.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold">מסמכים קשורים ויצירת קשר</h2>
          <p className="text-muted mt-2">
            השימוש באתר ובמידע הנמסר דרכו כפוף גם ל
            <Link to="/privacy" className="text-link underline">
              מדיניות הפרטיות
            </Link>{' '}
            ול
            <Link to="/accessibility" className="text-link underline">
              הצהרת הנגישות
            </Link>{' '}
            של האתר. שאלות לגבי תנאי השימוש ניתן להפנות דרך עמוד{' '}
            <Link to="/contact" className="text-link underline">
              צור קשר
            </Link>{' '}
            או בדוא"ל ל-
            <a href={EMAIL_HREF} className="ltr-inline text-link underline">
              {EMAIL}
            </a>
            .
          </p>
        </section>

        <p className="text-muted text-sm">
          © כל הזכויות שמורות ל-{COMPANY_LEGAL_NAME}, 2026. עודכן לאחרונה: {LAST_REVIEWED}.
        </p>
      </div>
    </div>
  );
}
